
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import { computed, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IModuleStateProps, statusType, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { Address, CountryRegionInfo, StateProvinceInfo } from '@msdyn365-commerce/retail-proxy';

import { AddressCommon } from '../../common/address-common';
import { AddressFormat } from '../../common/address-format';
import { AddressItemType } from '../../common/address-format.data';
import { AddressMetaData } from '../../common/address-meta-data';
import {
    AddressOperation, AddressType, IAddressResource, IAddressResponse
} from '../../common/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from '../../common/components/address-add';
import { AddressSelect, IAddressSelectProps } from '../../common/components/address-select';
import { AddressShow, IAddressShowProps } from '../../common/components/address-show';
import { WineClubDataStore } from '../wine-club-signup/wine-club-data';
import { ISmweCheckoutShippingAddressData } from './smwe-checkout-shipping-address.data';
import { ISmweCheckoutShippingAddressProps } from './smwe-checkout-shipping-address.props.autogenerated';

export interface IProps extends ISmweCheckoutShippingAddressProps<ISmweCheckoutShippingAddressData>, IModuleStateProps { }

export interface ICheckoutShippingAddressViewState {
    isShowAddress: boolean;
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
}

export interface ICheckoutShippingAddressViewProps extends IProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    addressListSelectedAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: ICheckoutShippingAddressViewState;
    CheckoutShippingAddress: IModuleProps;
    isUpdating: boolean;
    hasError: boolean;
    showAddress: IAddressShowProps;
    showAddressSelect: IAddressSelectProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * Address component
 * @extends {React.Component<ISmweCheckoutShippingAddressProps<ISmweCheckoutShippingAddressData>>}
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutShippingAddress extends React.Component<IProps> {
    @observable private currentOperation: AddressOperation;
    @observable private selectedAddress?: Address;
    @observable private addUpdateAddress: Address;
    @observable private countryRegionId: string = 'USA';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private customerAddresses: Address[];
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private isUpdating?: boolean;
    @observable private hasError?: boolean;
    @observable private addressListSelectedAddress: Address = {};

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private customerAccountNumber?: string;
    private resources: IAddressResource;

    constructor(props: IProps) {
        super(props);
        const { context, data, resources, telemetry } = this.props;

        this.addUpdateAddress = {};
        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.customerAccountNumber = context && context.request && context.request.user && context.request.user.customerAccountNumber;
        this.countryRegions = data.countryRegions.result || [];
        this.customerAddresses = data.address.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressFormat = new AddressFormat(context, this.countryRegions, new AddressMetaData({ ...resources }));
        this.addressCommon = new AddressCommon(context, resources, telemetry, this.customerAccountNumber);
        this.validationError = {};
    }

    public componentDidMount(): void {
        this.props.data.checkout.then(() => {
            this._setDefaultCountryRegionId();
            this._initModuleState();
        }).catch(e => this.props.telemetry.exception(e));

        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );
    }

    public render(): JSX.Element | null {
        if (!this._canShip()) {
            return null;
        }

        const { renderView, config } = this.props;

        const viewProps = {
            ...this.props,
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            addressListSelectedAddress: this.addressListSelectedAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            className: config.className,
            viewState: {
                isShowAddress: this.currentOperation === AddressOperation.Show && this.selectedAddress,
                isShowAddresList: this.currentOperation === AddressOperation.List && this.customerAddresses.length > 0,
                isShowAddOrUpdateAddress: this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update,
            },
            CheckoutShippingAddress: {
                moduleProps: this.props,
                className: classnames('ms-checkout-shipping-address', config.className)
            },
            showAddress: this._renderShowAddress(),
            showAddressSelect: this._renderSelectAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;

    }

    private _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        return AddressAddUpdate(
            {
                isUpdating: this.isUpdating,
                resources: this.resources,
                addressType: AddressType.Shipping,
                addressFormat: this.addressFormat.getAddressFormat((this.addUpdateAddress.ThreeLetterISORegionName) || this.countryRegionId),
                defaultCountryRegionId: this.countryRegionId,
                selectedAddress: this.addUpdateAddress,
                validationError: this.validationError,
                hasError: this.hasError,
                addressActionResponse: this.addressActionResponse,
                dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(this.resources.addressStateDefaultSelectionText, this.stateProvinceInfo, true),
                onInputChange: this._onAddressAddUpdateInputChange,
                onDropdownChange: this._onAddressAddUpdateDropdownChange,
                hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
                onSave: this.onSubmit,
                // tslint:disable-next-line: no-empty
                onCancel: !this.selectedAddress && !this.addressCommon.isAuthenticatedFlow() ? () => { } : this._resetView
            }
        );
    };

    private _renderSelectAddress = (): IAddressSelectProps => {
        return AddressSelect(
            {
                addressFormat: this.addressFormat,
                addresses: this.customerAddresses,
                resources: this.resources,
                selectedAddress: this.addressListSelectedAddress,
                onAddressOptionChange: this._onAddressOptionChange,
                hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
                onAddAddress: this._goToAddAddress,
                onSave: this._onSelectAddress,
                // tslint:disable-next-line: no-empty
                onCancel: !this.selectedAddress ? () => { } : this._resetView
            }
        );
    };

    private _renderShowAddress(): IAddressShowProps | null {
        if (this.selectedAddress) {
            return AddressShow({ address: this.selectedAddress, addressFormat: this.addressFormat.getAddressFormat(this.selectedAddress.ThreeLetterISORegionName || '') });
        }

        return null;
    }

    private _onAddressOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const addressRecordId = event.currentTarget.value;
        const selectedAddress = this.customerAddresses.find((address) => (address.RecordId || '').toString() === addressRecordId);
        if (selectedAddress) {
            this.addressListSelectedAddress = selectedAddress;
        }
    };

    private onAddressAddUpdate = (name: string, value: string) => {
        set(this.addUpdateAddress, { [name]: value });
    };

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
        this.onAddressAddUpdate(event.target.name, value);
    };

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAddUpdate(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    private _onAddressAddUpdateSubmit = (): void => {
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
            this.takeFocustoErrorField();
            return;
        }

        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow()) {
            response = this.currentOperation === AddressOperation.Update ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress) : this.addressCommon.addCustomerAddress(this.addUpdateAddress);
        } else {
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this.isUpdating = true;
        this._updateModuleState();

        response.then(
            (result: IAddressResponse) => {
                this.isUpdating = false;

                if (result.address) {
                    this.hasError = false;
                    this._onAddOrUpdateSuccess(result);
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                    this._updateModuleState();
                }
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
                this._updateModuleState();
            }
        );
    };

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
    };

    private _getStateProvinces = (): void => {
        if (!this.countryRegionId || !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)) {
            return;
        }

        // tslint:disable-next-line:no-floating-promises
        this.addressCommon.getStateProvinces(this.countryRegionId).then(
            (result: StateProvinceInfo[]) => {
                this.stateProvinceInfo = result;
            }).catch(e => this.props.telemetry.exception(e));
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request && request.channel && request.channel.ChannelCountryRegionISOCode;

        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _getDefaultAddress = (): Address | undefined => {
        if (this.customerAddresses) {
            const primaryAddress = this.customerAddresses.find((address: Address) => address.IsPrimary);
            return primaryAddress || (this.customerAddresses.length > 0 ? this.customerAddresses[0] : undefined);
        }
        return undefined;
    };

    private _forceDisable(): boolean {
        const store = WineClubDataStore.getInstance().get();
        const wineState = this.props.moduleState.getModuleByTypeName('checkout-club-info-collector');
        return store.deliveryType === 'PICKUP' && wineState.hasModuleState;
    }

    private _initModuleState = (): void => {
        let status: statusType = this._canShip() ? 'updating' : 'disabled';
        if (this._forceDisable()) {
            status = 'disabled';
            this._setShippingAddress(this.addressListSelectedAddress);      // DG: Force the shipping address to be {} instead of undefined. Undefined breaks API calls.
        }
        const { request } = this.props.context;
        this.props.moduleState.init({
            status: status,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit
        });

        const defaultAddress = this._getDefaultAddress();
        if (this._canShip()) {
            if (defaultAddress) {
                this._updateCurrentOperation(AddressOperation.Show, defaultAddress);
                this._setShippingAddress(defaultAddress);
                this._updateModuleState();
            } else {
                this._updateCurrentOperation(AddressOperation.Add);
            }
        } else {
            this.props.data.cart.then(() => {
                const { cart } = this.props.data;
                const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;
                // "GIFT CARD CHECK"
                if (cart && cart.result && cart.result.CartLines && cart.result.CartLines.filter(item => item.DeliveryMode === emailDeliveryModeCode).length === 1) {
                    if (!defaultAddress) {
                        this._updateCurrentOperation(AddressOperation.Add);
                    }
                }
            }).catch(e => this.props.telemetry.exception(e));
        }

        if (this._forceDisable()) { this.props.moduleState.onDisable(); }
    };

    // private _setBillingAddress = (newBillingAddress: Address): void => {
    //     if (this.props.data.checkout.result) {
    //         this.props.data.checkout.result.updateBillingAddress({newBillingAddress});
    //     }
    // }

    private onSubmit = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._onAddressAddUpdateSubmit();
                break;
            case AddressOperation.List:
                this._onSelectAddress();
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onCancel = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                if (!(!this.selectedAddress && !this.addressCommon.isAuthenticatedFlow())) {
                    this._resetView();
                }

                break;
            case AddressOperation.List:
                if (this.selectedAddress) {
                    this._resetView();
                }
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onEdit = (): void => {
        if (this.addressCommon.isAuthenticatedFlow() && this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.List, this.shippingAddress);
        } else if (this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.Update, this.shippingAddress);
        }

        this._updateModuleState();
    };

    private _canShip = (): boolean => {
        const { checkout } = this.props.data;
        const { request } = this.props.context;
        if (!checkout.result || !request.channel || checkout.result.checkoutCart.isEmpty) {
            return false;
        }
        // @ts-ignore: Type-checker not realizing above request.channel check
        return checkout.result.checkoutCart.cart.CartLines.some(cartLine => cartLine.DeliveryMode !== request.channel.PickupDeliveryModeCode);
    };

    private _onSelectAddress = () => {
        this._updateCurrentOperation(AddressOperation.Show, this.addressListSelectedAddress);
        this._setShippingAddress(this.addressListSelectedAddress);
        this._updateModuleState();
    };

    private _goToAddAddress = () => {
        this._setDefaultCountryRegionId();
        this._updateCurrentOperation(AddressOperation.Add);
    };

    private _onAddOrUpdateSuccess = (response: IAddressResponse) => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }

        if (response.address) {
            this._updateCurrentOperation(AddressOperation.Show, response.address);
            this._setShippingAddress(response.address);
            this._updateModuleState();
        }
    };

    private _setShippingAddress = (address: Address): void => {
        if (this.props.data.checkout.result) {
            const newShippingAddress = { ...address };

            if (address.ThreeLetterISORegionName && !newShippingAddress.TwoLetterISORegionName) {
                newShippingAddress.TwoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(address.ThreeLetterISORegionName);
            }

            // tslint:disable-next-line:no-floating-promises
            void this.props.data.checkout.result.updateShippingAddress({ newShippingAddress });
        }
    };

    @computed get shippingAddress(): Address | undefined {
        return this.props.data.checkout.result && this.props.data.checkout.result.shippingAddress;
    }

    private _resetView = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._updateCurrentOperation(this.addressCommon.isAuthenticatedFlow() ? AddressOperation.List : AddressOperation.Show, this.shippingAddress);
                break;
            default:
                this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
        }
        this._updateModuleState();
    };

    private _updateCurrentOperation = (operation: AddressOperation, selectedAddress?: Address) => {
        this.currentOperation = operation;
        this.selectedAddress = selectedAddress;

        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            this.addUpdateAddress = { ...this.selectedAddress } || {};
            set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId });
        } else if (this.currentOperation === AddressOperation.List) {
            this.addressListSelectedAddress = { ...this.selectedAddress } || {};
        }
    };

    private _updateModuleState = () => {
        if (this._forceDisable()) { this.props.moduleState.onDisable(); return; }
        if (this.currentOperation === AddressOperation.Show) {
            this.props.moduleState.onReady();
        } else if (this.isUpdating) {
            this.props.moduleState.onPending();
        } else {
            this.props.moduleState.onUpdating();
        }
    };
    private takeFocustoErrorField = () => {
        var errors = this.validationError;
        if (errors["Name"] !== null) {
            const shipping_addressname = document.getElementById("shipping_addressname");
            shipping_addressname?.focus();
        }
        else if (errors["Street"] !== null) {
            const shipping_addressstreet = document.getElementById("shipping_addressstreet");
            shipping_addressstreet?.focus();
        }
        else if (errors["City"] !== null) {
            const shipping_addresscity = document.getElementById("shipping_addresscity");
            shipping_addresscity?.focus();
        }
        else if (errors["State"] !== null) {
            const shipping_addressstate = document.getElementById("shipping_addressstate");
            shipping_addressstate?.focus();
        }
        else if (errors["ZipCode"] !== null) {
            const shipping_addresszipcode = document.getElementById("shipping_addresszipcode");
            shipping_addresszipcode?.focus();
        }
        else if (errors["Phone"] !== null) {
            const shipping_addressphone = document.getElementById("shipping_addressphone");
            shipping_addressphone?.focus();
        }
    };
}

export default CheckoutShippingAddress;